import { getSession } from './sessions';
import {
  POST_APPROVAL_PLAID_REQUEST_FAILED,
  POST_APPROVAL_PLAID_REQUEST_STARTED,
  POST_APPROVAL_PLAID_STATUS_RECEIVED,
} from '../lib/constants/ActionTypes';
import {
  InvalidPostApprovalResponse,
  normalizeError,
  normalizeStatusResponse,
  statusResource,
} from '../lib/utils/post_approval_plaid';

export const path = (applicationId, suffix = '') => ({
  resource:
    'api/applications/' + applicationId + '/post_approval_plaid' + suffix,
});

export const beginRequest = (applicationId) => (dispatch, getState) => {
  dispatch({
    type: POST_APPROVAL_PLAID_REQUEST_STARTED,
    payload: applicationId,
  });

  return getState().post_approval_plaid.get('request_generation');
};

const receiveStatus = (dispatch, generation, applicationId, response) => {
  const normalized = normalizeStatusResponse(response, applicationId);

  dispatch({
    type: POST_APPROVAL_PLAID_STATUS_RECEIVED,
    generation,
    payload: normalized,
  });

  return normalized;
};

const failRequest = (dispatch, generation, error) => {
  const normalized =
    error instanceof InvalidPostApprovalResponse
      ? {
          http_status: null,
          outcome: 'invalid_response',
          reason_code: 'invalid_response',
          can_continue: false,
          next_route_path: null,
          network: false,
        }
      : normalizeError(error);

  dispatch({
    type: POST_APPROVAL_PLAID_REQUEST_FAILED,
    generation,
    payload: normalized,
  });

  const requestError = new Error('Post-approval Plaid request failed');
  Object.assign(requestError, normalized, { generation });
  throw requestError;
};

const captureActiveContext = (getState, applicationId) => {
  const state = getState();
  const session = state.sessions.get('session');
  const sessionId = session.get('id');
  const sessionApplicationId = session.get('application_id');
  const publicKey = state.sessions.get('public_key');

  if (
    typeof sessionId !== 'string' ||
    !sessionId ||
    sessionApplicationId !== applicationId ||
    typeof publicKey !== 'string' ||
    !publicKey
  ) {
    return null;
  }

  return { sessionId, sessionApplicationId, publicKey };
};

const isActiveContext = (
  getState,
  applicationId,
  { sessionId, publicKey, generation } = {}
) => {
  const activeContext = captureActiveContext(getState, applicationId);
  const plaid = getState().post_approval_plaid;

  return (
    activeContext &&
    (sessionId === undefined || activeContext.sessionId === sessionId) &&
    (publicKey === undefined || activeContext.publicKey === publicKey) &&
    (generation === undefined ||
      (plaid.get('application_id') === applicationId &&
        plaid.get('request_generation') === generation))
  );
};

const startInContext = (dispatch, getState, applicationId, expectedContext) => {
  if (
    expectedContext &&
    !isActiveContext(getState, applicationId, expectedContext)
  ) {
    return null;
  }

  const context = captureActiveContext(getState, applicationId);
  if (!context) return null;

  return { ...context, generation: dispatch(beginRequest(applicationId)) };
};

export const fetchStatus =
  (applicationId, context) => async (dispatch, getState, api) => {
    const activeContext = startInContext(
      dispatch,
      getState,
      applicationId,
      context
    );
    if (!activeContext) return null;
    const { generation } = activeContext;

    try {
      const attempt = getState().post_approval_plaid.get('attempt');
      const resource = attempt.get('status_url')
        ? statusResource(applicationId, attempt.get('status_url'))
        : path(applicationId);
      const response = await api.get(resource);
      if (!isActiveContext(getState, applicationId, activeContext)) return null;
      return receiveStatus(dispatch, generation, applicationId, response);
    } catch (error) {
      if (!isActiveContext(getState, applicationId, activeContext)) return null;
      return failRequest(dispatch, generation, error);
    }
  };

export const createLinkToken =
  (applicationId, context) => async (dispatch, getState, api) => {
    const activeContext = startInContext(
      dispatch,
      getState,
      applicationId,
      context
    );
    if (!activeContext) return null;
    const { generation } = activeContext;
    const sessionContext = {
      sessionId: activeContext.sessionId,
      publicKey: activeContext.publicKey,
    };
    const priorAttemptId = getState().post_approval_plaid.getIn([
      'attempt',
      'attempt_id',
    ]);
    const canStartFreshAttempt =
      !priorAttemptId &&
      getState().post_approval_plaid.get('pre_attempt_can_continue') === true;
    let response;

    try {
      response = await api.post(path(applicationId, '/link_token'));

      if (!isActiveContext(getState, applicationId, activeContext)) {
        return null;
      }

      if (!response || typeof response.link_token !== 'string') {
        receiveStatus(dispatch, generation, applicationId, response);
        return null;
      }
    } catch (error) {
      if (!isActiveContext(getState, applicationId, activeContext)) {
        return null;
      }
      return failRequest(dispatch, generation, error);
    }

    if (!isActiveContext(getState, applicationId, activeContext)) {
      return null;
    }

    const token = {
      link_token: response.link_token,
      expiration: response.expiration,
    };

    const status = await dispatch(fetchStatus(applicationId, sessionContext));
    return isActiveContext(getState, applicationId, sessionContext) &&
      status?.kind === 'attempt' &&
      status.attempt.get('status') === 'link_ready' &&
      ((priorAttemptId && status.attempt.get('attempt_id') === priorAttemptId) ||
        canStartFreshAttempt)
      ? token
      : null;
  };

export const submit =
  (applicationId, { publicToken, accountId, idempotencyKey }, context) =>
  async (dispatch, getState, api) => {
    const activeContext = startInContext(
      dispatch,
      getState,
      applicationId,
      context
    );
    if (!activeContext) return null;
    const { generation } = activeContext;

    try {
      const response = await api.post(
        path(applicationId, '/submit'),
        {
          plaid: {
            public_token: publicToken,
            account_id: accountId,
          },
        },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      );

      if (!isActiveContext(getState, applicationId, activeContext)) return null;
      return receiveStatus(dispatch, generation, applicationId, response);
    } catch (error) {
      if (!isActiveContext(getState, applicationId, activeContext)) return null;
      return failRequest(dispatch, generation, error);
    }
  };

export const skip =
  (applicationId, context) => async (dispatch, getState, api) => {
    const activeContext = startInContext(
      dispatch,
      getState,
      applicationId,
      context
    );
    if (!activeContext) return null;
    const { generation } = activeContext;

    try {
      const response = await api.post(path(applicationId, '/skip'));
      if (!isActiveContext(getState, applicationId, activeContext)) return null;
      return receiveStatus(dispatch, generation, applicationId, response);
    } catch (error) {
      if (!isActiveContext(getState, applicationId, activeContext)) return null;
      return failRequest(dispatch, generation, error);
    }
  };

export const recoverSubmit =
  (applicationId, submitOptions, context) => async (dispatch, getState) => {
    if (!isActiveContext(getState, applicationId, context)) return null;

    const statusRequest = dispatch(
      fetchStatus(applicationId, {
        sessionId: context && context.sessionId,
        publicKey: context && context.publicKey,
      })
    );
    const recoveryGeneration =
      getState().post_approval_plaid.get('request_generation');
    const status = await statusRequest;

    if (
      !isActiveContext(getState, applicationId, {
        sessionId: context && context.sessionId,
        publicKey: context && context.publicKey,
        generation: recoveryGeneration,
      })
    ) {
      return null;
    }

    if (
      status.kind === 'attempt' &&
      status.attempt.get('status') === 'link_ready'
    ) {
      return dispatch(
        submit(applicationId, submitOptions, {
          sessionId: context && context.sessionId,
          publicKey: context && context.publicKey,
        })
      );
    }

    return status;
  };

export const refreshAfterTerminal = () => async (dispatch, getState) => {
  const state = getState();
  const attempt = state.post_approval_plaid.get('attempt');

  if (!attempt.isTerminal) return null;

  const applicationId = state.sessions.getIn(['session', 'application_id']);
  const activeContext = captureActiveContext(getState, applicationId);
  if (!activeContext) return null;
  const requestGeneration = state.post_approval_plaid.get('request_generation');
  const canApplyResponse = () => {
    const currentState = getState();

    return (
      isActiveContext(getState, applicationId, activeContext) &&
      currentState.post_approval_plaid.get('request_generation') ===
        requestGeneration
    );
  };

  await dispatch(getSession({ canApplyResponse }));
  if (!canApplyResponse()) return null;

  return getState().sessions.get('session');
};
